import { useEffect, useState } from 'react';
import { Alert, Button, Group, Modal, NumberInput, Stack, Switch, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ApiError, updateZoneMetadata, type Zone } from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';

interface Props {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly zone: Zone;
  readonly onSaved: () => void;
}

export function ZoneMetadataModal({ opened, onClose, zone, onSaved }: Props): JSX.Element {
  const [label, setLabel] = useState(zone.zoneLabel);
  const [capacity, setCapacity] = useState<number | ''>(
    // Zone DTO doesn't carry raw capacity; seed with current total units as a sane baseline.
    zone.totalUnits,
  );
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setLabel(zone.zoneLabel);
    setCapacity(zone.totalUnits);
    setActive(true);
    setErr(null);
  }, [opened, zone]);

  async function handleSave(): Promise<void> {
    setErr(null);
    if (label.trim().length === 0) {
      setErr('區域名稱不可為空');
      return;
    }
    if (typeof capacity !== 'number' || capacity < 1) {
      setErr('容量必須 ≥ 1');
      return;
    }
    setSaving(true);
    try {
      await updateZoneMetadata(zone.resourceId, {
        zoneLabel: label.trim(),
        capacity,
        active,
      });
      notifications.show({
        color: 'green',
        title: '區域資料已更新',
        message: `${label} · 容量 ${capacity} · ${active ? '啟用中' : '已停用'}`,
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`區域設定 · ${zone.zoneLabel}`} centered size="sm">
      <Stack gap="sm">
        <TextInput
          label="區域名稱"
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
          required
        />
        <NumberInput
          label="總容量（人）"
          description="供 overbook / capacity check 使用；通常等於所有 unit 容量加總"
          value={capacity}
          onChange={(v) => setCapacity(typeof v === 'number' ? v : '')}
          min={1}
          required
        />
        <Switch
          label="啟用區域"
          description="停用後不會出現在新增預訂的區域選擇"
          checked={active}
          onChange={(e) => setActive(e.currentTarget.checked)}
          color={tokens.color.mantine['primary']}
        />
        <Text size="xs" c="dimmed">
          價格與規則分別透過各自的「編輯」按鈕。
        </Text>
        {err && (
          <Alert color="red" variant="light">
            {err}
          </Alert>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="light" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button
            color={tokens.color.mantine['primary']}
            onClick={() => void handleSave()}
            loading={saving}
          >
            儲存
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
